import React, { useState, useEffect, Fragment } from 'react';
import { API, Storage } from 'aws-amplify';
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Box,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
  Tooltip,
  FormLabel,
  FormControlLabel,
  FormControl,
  IconButton,
} from '@mui/material';
import { Cached } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt-br';
import dayjs from 'dayjs';
import Loading from 'util/loading';
import ReportFile from './reportFile';
import { red, indigo, green, grey, blue } from '@mui/material/colors';

const styles = {
  cardAction: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  cards: {
    width: '100%',
    marginTop: 1,
    minHeight: 240,
  },
  cardContents: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: '18px',
  },
  topBox: {
    columnGap: 12,
  },
  select: {
    width: '420px',
  },
  datePicker: {
    width: '300px',
  },
};

const compareFiles = function (a, b) {
  if (a.lastModified > b.lastModified) {
    return -1;
  }

  if (a.lastModified < b.lastModified) {
    return 1;
  }

  return 0;
};

const filterFiles = function (aFile) {
  const fileExtension = aFile.key.split('.').at(-1);
  return plain(fileExtension).includes('csv');
};

function plain(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const REPORTKEY_BYSVA = 0;
const REPORTKEY_BYOPERATOR = 1;

const GwReportsPage = () => {
  const [loading, setLoading] = useState(false);

  const [operators, setOperators] = useState([]);
  const [urns, setUrns] = useState([]);
  const [s3Files, setS3Files] = useState([]);

  const [alert, setAlert] = useState({
    severity: 'info',
    message: '',
    open: false,
  });

  // se verdadeiro, lista os SVAs de um operador
  // se falso, lista os operadores de um SVA
  const [reportType, setReportType] = useState(REPORTKEY_BYSVA);

  const [currentSva, setCurrentSva] = useState('*');
  const [currentOperator, setCurrentOperator] = useState('*');

  // Create a Date for the first day of the current month (at 00:00:00 local time)
  const now = new Date();
  const firstDayOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const [startDate, setStartDate] = useState(dayjs(firstDayOfThisMonth));
  const [finishDate, setFinishDate] = useState(dayjs(firstDayOfThisMonth));

  const minDate = '2020-05-10';

  const compareOperators = function (a, b) {
    if (plain(a.name) > plain(b.name)) return 1;
    if (plain(a.name) < plain(b.name)) return -1;
    return 0;
  };

  const compareUrns = function (a, b) {
    if (plain(a.urn) > plain(b.urn)) return 1;
    if (plain(a.urn) < plain(b.urn)) return -1;
    return 0;
  };

  async function listFiles() {
    try {
      setLoading(true);
      const prefix = 'neogateway/';

      let hasNextToken = false;
      const files = [];
      let token = null;

      do {
        const f = await Storage.list(prefix, {
          level: 'public',
          pageSize: 1000,
          nextToken: token,
        });

        files.push(...f.results);
        hasNextToken = Boolean(f.hasNextToken);
        if (hasNextToken) {
          token = f.nextToken;
        }
      } while (hasNextToken);
      console.log(files);

      setS3Files(files.filter(filterFiles).sort(compareFiles));
      console.log(files.filter(filterFiles).sort(compareFiles));
    } catch (error) {
      console.log(error);
      setAlert({
        message: error.toString(),
        severity: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  }

  function monthDiff() {
    let months;
    months =
      (parseInt(finishDate.format('YYYY')) -
        parseInt(startDate.format('YYYY'))) *
      12;
    months -= parseInt(startDate.format('M'));
    months += parseInt(finishDate.format('M'));
    return months <= 0 ? 0 : months;
  }

  async function listOperators() {
    try {
      setLoading(true);
      const apiName = 'lambdaApi';
      const path = '/listOperators';
      const response = await API.get(apiName, path);
      const operatorsList = [
        {
          id: '*',
          name: '*',
        },
      ];
      operatorsList.push(...response.sort(compareOperators));

      setOperators(operatorsList);
    } catch (error) {
      console.log(error);
      setAlert({
        message: error.toString(),
        severity: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  }

  async function listUrns() {
    try {
      setLoading(true);
      const apiName = 'lambdaApi';
      const path = '/listUrns';
      const response = await API.get(apiName, path);
      // console.log(response);
      const urnList = [
        {
          urn: '*',
        },
      ];
      urnList.push(...response.sort(compareUrns));
      setUrns(urnList);
    } catch (error) {
      console.log(error);
      setAlert({
        message: error.toString(),
        severity: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    listFiles();
    listUrns();
    listOperators();

    return function cleanup() {};
  }, []);

  function handleCloseAlert() {
    setAlert({
      message: '',
      severity: 'info',
      open: false,
    });
  }

  function handleChangeListKey(aEvent) {
    setReportType(JSON.parse(aEvent.target.value));
  }

  function handleChangeSva(aEvent) {
    setCurrentSva(aEvent.target.value);
  }

  function handleChangeOperator(aEvent) {
    setCurrentOperator(aEvent.target.value);
  }

  function handleChangeStartDate(aValue) {
    setStartDate(aValue);
  }

  async function handleRefreshFiles() {
    await listFiles();
  }

  function handleChangeFinishDate(aValue) {
    setFinishDate(aValue);
  }

  async function handleGenerateReport() {
    if (monthDiff() > 12) {
      setAlert({
        message: 'Diferença maior que 12 meses',
        severity: 'warning',
        open: true,
      });
      return;
    }

    if (finishDate.diff(startDate) < 0) {
      setAlert({
        message: 'Data de fim menor que a de início',
        severity: 'warning',
        open: true,
      });
      return;
    }

    try {
      setLoading(true);

      const apiName = 'lambdaApi';
      const path = '/report';
      let inputs = {
        body: {
          start: startDate.format().split('T')[0],
          finish: finishDate.format().split('T')[0],
        },
        headers: {},
      };

      switch (reportType) {
        case REPORTKEY_BYOPERATOR:
          {
            inputs.body.operator = currentOperator;
          }
          break;

        case REPORTKEY_BYSVA:
          {
            inputs.body.sva = currentSva;
          }
          break;
      }

      const response = await API.post(apiName, path, inputs);
      setAlert({
        message:
          'Solicitação enviada. Aguarde alguns minutos e recarrege a lista de arquivos.',
        severity: 'success',
        open: true,
      });
    } catch (error) {
      console.log(error);
      setAlert({
        message: error.toString(),
        severity: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Fragment>
      <Loading loading={loading} />

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={(e) => handleCloseAlert()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>

      <Card sx={styles.cards}>
        <CardContent sx={styles.cardContents}>
          {/* CheckBox para selecionar sva ou operadora */}

          <FormControl variant="standard">
            <FormLabel id="demo-controlled-radio-buttons-group">
              Chave
            </FormLabel>
            <RadioGroup
              row
              name="controlled-radio-buttons-group"
              value={reportType}
              onChange={(e) => handleChangeListKey(e)}
            >
              <FormControlLabel
                value={REPORTKEY_BYSVA}
                control={<Radio />}
                label="Por SVA"
              />
              <FormControlLabel
                value={REPORTKEY_BYOPERATOR}
                control={<Radio />}
                label="Por operadora"
              />
            </RadioGroup>
          </FormControl>

          {reportType === REPORTKEY_BYSVA && (
            <FormControl variant="standard">
              <FormLabel>SVA</FormLabel>

              <Select
                name="currentSvaSelect"
                value={currentSva}
                onChange={(e) => handleChangeSva(e)}
                variant="standard"
                sx={styles.select}
              >
                {urns.map((item, idx) => (
                  <MenuItem key={idx} value={item.urn}>
                    {item.urn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Select sva ou operadora */}
          {reportType === REPORTKEY_BYOPERATOR && (
            <FormControl variant="standard">
              <FormLabel>SVA</FormLabel>
              <InputLabel>Operadora</InputLabel>

              <Select
                name="currentOperatorSelect"
                value={currentOperator}
                onChange={(e) => handleChangeOperator(e)}
                variant="standard"
                sx={styles.select}
              >
                {operators.map((item, idx) => (
                  <MenuItem key={idx} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={styles.datePicker}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-BR"
            >
              <DatePicker
                views={['year', 'month']}
                minDate={dayjs(minDate)}
                maxDate={dayjs(new Date())}
                label="Data de início"
                value={startDate}
                onChange={(newValue) => handleChangeStartDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl sx={styles.datePicker}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-BR"
            >
              <DatePicker
                views={['year', 'month']}
                minDate={dayjs(minDate)}
                maxDate={dayjs(new Date())}
                label="Data de fim"
                value={finishDate}
                onChange={(newValue) => handleChangeFinishDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </FormControl>
        </CardContent>
        <CardActions disableSpacing sx={styles.cardAction}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleGenerateReport()}
          >
            Enviar
          </Button>
        </CardActions>
      </Card>

      <Card sx={styles.cards}>
        <CardHeader
          title="Lista dos últimos relatórios"
          action={
            <Tooltip title="Refresh">
              <IconButton
                onClick={(e) => handleRefreshFiles(e)}
                edge="end"
                sx={{
                  color: 'white',
                  backgroundColor: green[500],
                  marginLeft: 'auto',
                  height: 'fit-content',
                  marginRight: 4,
                }}
              >
                <Cached />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent>
          {s3Files.map((file, idx) => (
            <ReportFile key={idx} file={file} onChange={listFiles} />
          ))}
        </CardContent>
      </Card>
    </Fragment>
  );
};

export default GwReportsPage;
